import {
  Directive,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  inject,
  input,
  output,
  HostListener,
} from '@angular/core';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';

/**
 * Directive that provides AA-compliant accessibility features for dialogs and sidebars.
 *
 * Features:
 * - Focus trap: Keeps focus within the dialog
 * - Focus management: Saves and restores focus on open/close
 * - Escape key handling: Emits event when Escape is pressed
 * - Scroll lock: Prevents body scrolling while dialog is open
 *
 * @example
 * ```html
 * <div
 *   appDialogA11y
 *   [dismissible]="true"
 *   (escapePressed)="closeDialog()"
 *   role="dialog"
 *   aria-modal="true"
 *   aria-labelledby="dialog-title"
 * >
 *   <h2 id="dialog-title">Dialog Title</h2>
 *   <p>Dialog content...</p>
 * </div>
 * ```
 */
@Directive({
  selector: '[appDialogA11y]',
  standalone: true,
})
export class DialogA11yDirective implements AfterViewInit, OnDestroy {
  private readonly focusTrapFactory = inject(FocusTrapFactory);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  private focusTrap?: FocusTrap;
  private previouslyFocusedElement?: HTMLElement;
  private originalBodyOverflow?: string;

  /**
   * Whether the dialog can be dismissed with the Escape key.
   * When true, pressing Escape will emit the escapePressed event.
   */
  dismissible = input<boolean>(true);

  /**
   * Whether to prevent body scrolling while the dialog is open.
   */
  lockScroll = input<boolean>(true);

  /**
   * Emitted when the Escape key is pressed and the dialog is dismissible.
   */
  escapePressed = output<void>();

  ngAfterViewInit(): void {
    this.savePreviousFocus();
    this.createFocusTrap();
    this.lockBodyScroll();
  }

  ngOnDestroy(): void {
    this.destroyFocusTrap();
    this.restorePreviousFocus();
    this.unlockBodyScroll();
  }

  private savePreviousFocus(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
  }

  private restorePreviousFocus(): void {
    if (this.previouslyFocusedElement?.focus) {
      // Use setTimeout to ensure the element is visible before focusing
      setTimeout(() => {
        this.previouslyFocusedElement?.focus();
      }, 0);
    }
  }

  private createFocusTrap(): void {
    const element = this.elementRef.nativeElement;
    this.focusTrap = this.focusTrapFactory.create(element);
    this.focusTrap.focusInitialElementWhenReady();
  }

  private destroyFocusTrap(): void {
    if (this.focusTrap) {
      this.focusTrap.destroy();
      this.focusTrap = undefined;
    }
  }

  private lockBodyScroll(): void {
    if (this.lockScroll()) {
      this.originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }
  }

  private unlockBodyScroll(): void {
    if (this.lockScroll()) {
      document.body.style.overflow = this.originalBodyOverflow ?? '';
    }
  }

  @HostListener('keydown', ['$event'])
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.dismissible()) {
      event.preventDefault();
      event.stopPropagation();
      this.escapePressed.emit();
    }
  }
}
